import React, { useState, Fragment } from "react";
import { IconButton, Grid, Typography, Button } from "@mui/material";
import { makeStyles } from '@mui/styles';
import { usePermissions } from "react-admin";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import FolderIcon from "@mui/icons-material/FolderOpen";
import HomeIcon from "@mui/icons-material/HomeOutlined";
import FileIcon from "@mui/icons-material/InsertDriveFile";
import GetApp from "@mui/icons-material/GetApp";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Link as RouterLink } from "react-router-dom";
import Link from "@mui/material/Link";
import { useClientContext } from "contexts/client";
import ConfirmDelete from "./ConfirmDelete";
import CreateFolder from "./CreateFolder";
import Dropzone from "react-dropzone";
import ImagePreviewModal from "./ImagePreview";
import { orderBy } from "lodash";
import { useLocation } from "react-router-dom";
import { createClient } from "webdav/web";
import { API_ROOT } from "transport-layer";
import { useQuery, useMutation} from "@tanstack/react-query";


const getDavClient = (davRoot) => {
  const token = localStorage.getItem("token");

  const client = createClient(
    `${API_ROOT}/${process.env.REACT_APP_CLIENT}_dav/${davRoot}`,
    {
      token: {
        access_token: token,
        token_type: "Bearer",
      },
    }
  );
  return client;
};

const useStyles = makeStyles((theme) => ({
  link: {
    display: "flex",
  },
  icon: {
    marginRight: theme.spacing(0.5),
    width: 24,
    height: 24,
    fill: "rgb(31, 119, 180)",
  },
}));


function humanFileSize(bytes, si = false, dp = 1) {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + " B";
  }

  const units = si
    ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
    : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (
    Math.round(Math.abs(bytes) * r) / r >= thresh &&
    u < units.length - 1
  );

  return bytes.toFixed(dp) + " " + units[u];
}

const FileView = (props) => {
  const { permissions } = usePermissions();
  const classes = useStyles();

  const location = useLocation();
  const pathLocation = `${location.pathname}${decodeURI(location.search)}`;

  const { state: client } = useClientContext();
  const clientPrefix = client.client.resourcePrefix;
  const CLIENT_ADMIN_ROLE = `${clientPrefix}_admin`;
  const [sortData, changeSortData] = useState({
    column: "basename",
    direction: "asc",
  });

  const [ imagePreviewOpen, setImagePreviewOpen ] = useState(false);
  const [ imagePreviewSource, setImagePreviewSource ] = useState('');
  const [ imagePreviewContentType, setImagePreviewContentType ] = useState('');
  
  const prevPathLocation = React.useRef(pathLocation);

  const basePathFolderRoot = location.pathname.replace("/files/", "");

  const davclient = getDavClient(location.pathname.replace("/files/", ""));

  const getDavRootFrompath = (pathname) => {
    const davRoot = pathname.replace("/files/", "").replace("?", "");
    return davRoot
  };

  const getData = (newPath) => {  
    return davclient
      .getDirectoryContents(newPath.split("?")[1] || "/")
      .then((data) => {
        return {
          data: data,
        };
      });
  };

  const davDownloadMutation = useMutation({
    mutationFn: (file) => {
      return davclient.getFileContents(file.filename, {details: {isDetailed: true}})
    },
    onSuccess: (data, variables, context) => {
      var blob = new Blob([data.data], {type: variables['mime']});
      var link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = variables.basename;
      link.click();
    },
  })


  const davCreateFolderMutation = useMutation({
    mutationFn: (foldername) => {
      const serverPath = pathLocation.split('?')[1] || '/'
      return davclient.createDirectory(`${serverPath}/${foldername}`)
    },
    onSuccess: (data, variables, context) => {
      refetch();
    },
  })

  const davDeleteMutation = useMutation({
    mutationFn: (filepath) => {
      return davclient.deleteFile(`${filepath}`)
    },
    onSuccess: (data, variables, context) => {
      refetch();
    },
  })


  const davPreviewMutation = useMutation({
    mutationFn: (filepath) => {
      return davclient.getFileContents(filepath, {details: {isDetailed: true}})
    },
    onSuccess: (data, variables, context) => {
      return new Promise((resolve, reject) => { 
        const contentType = data.headers['content-type']
        var blob = new Blob([data.data],{type: contentType});
        var reader = new FileReader();
        reader.onload = function(evt){
            var dataurl = evt.target.result;
            setImagePreviewSource(dataurl)
            setImagePreviewContentType(contentType)
            setImagePreviewOpen(true)
            resolve(dataurl)
        };
        reader.readAsDataURL(blob); 
      })
    }
  })


  const davUploadMutation = useMutation({
    mutationFn: (file) => {
      const serverPath = pathLocation.split('?')[1] || '/'
      return davclient.putFileContents(`${serverPath}/${file.name}`, file.data, { overwrite: true })
    },
    onSuccess: (data, variables, context) => {
      refetch();
    },
  })


function loadAsUrl(theFile, currentPath, pathname) {
  var reader = new FileReader();

  reader.onload = function (evt) {

  };

  reader.onloadend = () => {
    davUploadMutation.mutate({
      name: theFile.name,
      data: reader.result
  })
  };

  reader.readAsArrayBuffer(theFile);
}

  const {
    isPending,
    error,
    data: pathObject,
    refetch,
  } = useQuery({
    queryKey: ["files", pathLocation],
    queryFn: () => getData(pathLocation),
  });

  React.useEffect(() => {
    // Reset the pageParam when the filter changes
    if (prevPathLocation.current !== pathLocation) {
      refetch();
      prevPathLocation.current = pathLocation;
    }
  }, [location, pathLocation, refetch]);

  if (isPending) return "Loading...";

  if (error) return "An error has occurred: " + error.message;


  const icon = (entry) => {
    if (entry.type === "directory") {
      return (
        <IconButton style={{ padding: 8 }} size="large">
          <FolderIcon />
        </IconButton>
      );
    }
    if (entry.type === "file") {
      return (
        <IconButton
          style={{ padding: 8 }}
          onClick={(e) => {
            davPreviewMutation.mutate(entry.filename)
            // previewFile(entry)
          }}
          size="large">
          <FileIcon />
        </IconButton>
      );
    }
  };

  const breadCrumbs = (pl) => {
    const split = pl
      .replace(basePathFolderRoot, "")
      .split("/")
      .filter((e) => e !== "");
    return (
      <Breadcrumbs
        separator="›"
        aria-label="files-breadcrumb"
        style={{ padding: 8, minHeight: 40, paddingTop: 24 }}
      >
        <RouterLink
          component={RouterLink}
          color="inherit"
          className={classes.link}
          relative="path"
          to={`#/files/${basePathFolderRoot}`}
          exact
        >
          <HomeIcon className={classes.icon} />
        </RouterLink>

        {pl
          ? split.map((e, i) => {
              if (i + 1 !== split.length) {
                const newPath = "/" + split.filter((v, k) => k <= i).join("/");
                return (
                  <RouterLink
                    component={RouterLink}
                    color="inherit"
                    to={`/files/${basePathFolderRoot}?${newPath}`}
                    key={i}
                    exact
                  >
                    {decodeURI(e)}
                  </RouterLink>
                );
              } else {
                return (
                  <Typography color="textPrimary" key={i}>{decodeURI(e)}</Typography>
                );
              }
            })
          : null}
      </Breadcrumbs>
    );
  };

  const sortedPathObject = orderBy(
    pathObject ? pathObject.data : [],
    [sortData.column],
    [sortData.direction]
  );

  return (
    <Fragment>
      <ImagePreviewModal
        isOpen={imagePreviewOpen} 
        handleClose={() => {
          setImagePreviewOpen(false)
          setImagePreviewSource('')
          setImagePreviewContentType('')
        }}
        imagePreviewSource={imagePreviewSource}
        imagePreviewContentType={imagePreviewContentType}
      />
      <Grid
        container
        style={{
          minHeight: 64,
          alignItems: "flex-start",
          paddingRight: 0,
          justifyContent: "space-between",
        }}
      >
        <Grid item>{breadCrumbs(getDavRootFrompath(pathLocation))}</Grid>
        {(permissions === "site_admin" ||
          permissions === CLIENT_ADMIN_ROLE) && (
          <Grid
            item
            style={{
              minHeight: 40,
              paddingTop: 24,
              display: "flex",
            }}
          >
            <div>
              <input
                accept="*/*"
                className={classes.input}
                style={{ display: "none" }}
                id="outlined-button-file"
                multiple
                type="file"
                onChange={(event) => {
                  Object.keys(event.target.files).forEach((key) => {
                    loadAsUrl(event.target.files[key], pathLocation, location.pathname)
                  });
                }}
              />
              <label htmlFor="outlined-button-file">
                <Button
                  color="primary"
                  variant="outlined"
                  component="span"
                  className={classes.button}
                  startIcon={<CloudUploadIcon />}
                >
                  Upload
                </Button>
              </label>
            </div>
            <CreateFolder onConfirm={(folderName) => davCreateFolderMutation.mutate(folderName)} />
            {/* <SearchFiles /> */}
          </Grid>
        )}
      </Grid>

      <Dropzone
        noClick
        onDrop={(acceptedFiles) => {
          acceptedFiles.forEach((file) => {
            loadAsUrl(file, pathLocation, location.pathname)
          });
        }}
      >
        
        {({ getRootProps, getInputProps }) => (
          <div
            {...getRootProps({
              onClick: event => event.stopPropagation()
            })}
            style={{ outline: "none" }}
            key="cntnr"
          >
            <input {...getInputProps()} />
            <TableContainer component={Paper} key="tk">
              <Table className={classes.table} aria-label="simple table">
                <TableHead key="th">
                  <TableRow key="tr1">
                    <TableCell align="left">
                      <TableSortLabel
                        active={sortData.column === "type"}
                        direction={
                          sortData.column === "type"
                            ? sortData.direction
                            : "asc"
                        }
                        onClick={(e, x) => {
                          changeSortData({
                            column: "type",
                            direction:
                              sortData.direction === "asc" ? "desc" : "asc",
                          });
                        }}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="left">
                      <TableSortLabel
                        active={sortData.column === "basename"}
                        direction={
                          sortData.column === "basename"
                            ? sortData.direction
                            : "asc"
                        }
                        onClick={(e, x) => {
                          changeSortData({
                            column: "basename",
                            direction:
                              sortData.direction === "asc" ? "desc" : "asc",
                          });
                        }}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortData.column === "size"}
                        direction={
                          sortData.column === "size"
                            ? sortData.direction
                            : "asc"
                        }
                        onClick={(e, x) => {
                          changeSortData({
                            column: "size",
                            direction:
                              sortData.direction === "asc" ? "desc" : "asc",
                          });
                        }}
                      >
                        Size
                      </TableSortLabel>
                    </TableCell>

                    <TableCell align="center"></TableCell>
                    {(permissions === "site_admin" ||
                      permissions === CLIENT_ADMIN_ROLE) && (
                      <TableCell align="center"></TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedPathObject
                    ? sortedPathObject.map((row) => {
                        if (row.type === "directory") {
                          return (
                            <TableRow key={row.name} hover>
                              <TableCell align="center" style={{ width: "5%" }}>
                                {icon(row)}
                              </TableCell>

                              <TableCell
                                component="th"
                                scope="row"
                                style={{ width: "60%" }}
                              >
                                <Link
                                  component={RouterLink}
                                  color="inherit"
                                  to={`?${encodeURI(
                                    row.filename
                                  )}`}
                                >
                                  {row.basename}                
                                </Link>
                              </TableCell>
                              <TableCell align="right"></TableCell>

                              <TableCell
                                align="center"
                                style={{ width: "5%" }}
                              ></TableCell>

                              {(permissions === "site_admin" ||
                                permissions === CLIENT_ADMIN_ROLE) && (
                                <TableCell
                                  align="center"
                                  style={{ width: "5%" }}
                                >
                                  <ConfirmDelete
                                    filename={row.basename}
                                    row={row}
                                    onConfirm={() => {                                      
                                      davDeleteMutation.mutate(`${row.filename}/`)
                                    }}
                                  />
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        }
                        if (row.type === "file") {
                          return (
                            <TableRow key={row.name} hover>
                              <TableCell align="center" style={{ width: "5%" }}>
                                {icon(row)}
                              </TableCell>

                              <TableCell
                                component="th"
                                scope="row"
                                style={{ width: "60%" }}
                              >
                                {row.basename}
                              </TableCell>
                              <TableCell align="right">
                                {humanFileSize(row.size)}
                              </TableCell>

                              <TableCell align="center" style={{ width: "5%" }}>
                                <IconButton
                                  style={{ padding: 8 }}
                                  onClick={(e) => davDownloadMutation.mutate(row)}
                                  size="large">
                                  <GetApp />
                                </IconButton>
                              </TableCell>

                              {(permissions === "site_admin" ||
                                permissions === CLIENT_ADMIN_ROLE) && (
                                <TableCell
                                  align="center"
                                  style={{ width: "5%" }}
                                >
                                  <ConfirmDelete
                                    filename={row.basename}
                                    row={row}
                                    onConfirm={() => {                                      
                                      davDeleteMutation.mutate(`${row.filename}`)
                                    }}
                                  />
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        }
                        return null;
                      })
                    : null}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}
        
      </Dropzone>
    </Fragment>
  );
};

export default FileView;
